import { useCallback, useEffect, useId, useRef, useState } from "react"

import { useSession } from "@clerk/nextjs"
import { isClerkAPIResponseError } from "@clerk/nextjs/errors"
import type {
  SessionVerificationLevel,
  SessionVerificationResource,
} from "@clerk/nextjs/types"
import { Button } from "@coval/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { Input } from "@coval/ui/input"
import { Label } from "@coval/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"

type Factor =
  | NonNullable<SessionVerificationResource["supportedFirstFactors"]>[number]
  | NonNullable<SessionVerificationResource["supportedSecondFactors"]>[number]
function supported(factor: Factor, secondFactor: boolean) {
  if (secondFactor) {
    return (
      factor.strategy === "phone_code" ||
      factor.strategy === "totp" ||
      factor.strategy === "backup_code"
    )
  }
  // SSO preparation is experimental; this launch supports Clerk's prebuilt reverification methods: https://clerk.com/docs/guides/secure/reverification#caveats.
  return (
    factor.strategy === "password" ||
    factor.strategy === "email_code" ||
    factor.strategy === "phone_code" ||
    factor.strategy === "passkey"
  )
}
function factorLabel(factor: Factor) {
  switch (factor.strategy) {
    case "password":
      return "Password"
    case "email_code":
      return `Email ${factor.safeIdentifier}`
    case "phone_code":
      return `Text ${factor.safeIdentifier}`
    case "passkey":
      return "Passkey"
    case "totp":
      return "Authenticator app"
    case "backup_code":
      return "Backup code"
    case "enterprise_sso":
      return "Single sign-on"
  }
}
export function ClerkReverificationDialog({
  level,
  onComplete,
  onCancel,
}: {
  level: SessionVerificationLevel
  onComplete: () => void
  onCancel: () => void
}) {
  const { session } = useSession()
  const [verification, setVerification] =
    useState<SessionVerificationResource | null>(null)
  const [selected, setSelected] = useState("0")
  const methodSelectId = useId()
  const [value, setValue] = useState("")
  const [prepared, setPrepared] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef({ request: 0, mounted: true })
  const sessionRef = useRef(session)
  sessionRef.current = session
  const completeRef = useRef(onComplete)
  completeRef.current = onComplete
  const verificationRef = useRef(verification)
  verificationRef.current = verification
  const completed = useRef(false)
  const factors =
    (verification?.status === "needs_second_factor"
      ? verification.supportedSecondFactors
      : verification?.supportedFirstFactors
    )?.filter((item) =>
      supported(item, verification?.status === "needs_second_factor"),
    ) ?? []
  const factor = factors[Number(selected)]
  const handle = useCallback(
    async (task: () => Promise<SessionVerificationResource>) => {
      const request = ++generation.current.request
      setBusy(true)
      setError(null)
      try {
        const next = await task()
        if (
          !generation.current.mounted ||
          request !== generation.current.request
        ) {
          return
        }
        if (next.status === "complete") {
          if (!completed.current) {
            completed.current = true
            completeRef.current()
          }
        } else {
          if (next.status !== verificationRef.current?.status) {
            setSelected("0")
            setPrepared(false)
            setValue("")
          }
          setVerification(next)
        }
      } catch (cause) {
        if (
          generation.current.mounted &&
          request === generation.current.request
        ) {
          setError(
            isClerkAPIResponseError(cause)
              ? cause.errors[0]?.longMessage || cause.message
              : "Could not verify your identity. Please retry.",
          )
        }
      } finally {
        if (
          generation.current.mounted &&
          request === generation.current.request
        ) {
          setBusy(false)
        }
      }
    },
    [],
  )
  useEffect(() => {
    const lifecycle = generation.current
    lifecycle.mounted = true
    const activeSession = sessionRef.current
    if (activeSession) {
      void handle(() => activeSession.startVerification({ level }))
    }
    return () => {
      lifecycle.mounted = false
      lifecycle.request++
    }
  }, [session?.id, level, handle])
  const submit = () => {
    if (!session || !factor || busy) {
      return
    }
    if (factor.strategy === "passkey") {
      void handle(() => session.verifyWithPasskey())
      return
    }
    if (
      (factor.strategy === "email_code" || factor.strategy === "phone_code") &&
      !prepared
    ) {
      void handle(async () => {
        const next =
          verification?.status === "needs_second_factor" &&
          factor.strategy === "phone_code"
            ? await session.prepareSecondFactorVerification({
                strategy: "phone_code",
                phoneNumberId: factor.phoneNumberId,
              })
            : factor.strategy === "email_code"
              ? await session.prepareFirstFactorVerification({
                  strategy: "email_code",
                  emailAddressId: factor.emailAddressId,
                })
              : await session.prepareFirstFactorVerification({
                  strategy: "phone_code",
                  phoneNumberId: factor.phoneNumberId,
                })
        setPrepared(true)
        return next
      })
      return
    }
    if (!value) {
      return
    }
    void handle(() => {
      if (
        verification?.status === "needs_second_factor" &&
        (factor.strategy === "phone_code" ||
          factor.strategy === "totp" ||
          factor.strategy === "backup_code")
      ) {
        return session.attemptSecondFactorVerification({
          strategy: factor.strategy,
          code: value,
        })
      }
      if (factor.strategy === "password") {
        return session.attemptFirstFactorVerification({
          strategy: "password",
          password: value,
        })
      }
      if (
        factor.strategy === "phone_code" ||
        factor.strategy === "email_code"
      ) {
        return session.attemptFirstFactorVerification({
          strategy: factor.strategy,
          code: value,
        })
      }
      throw new Error("Choose another verification method.")
    })
  }
  const needsCode =
    factor?.strategy !== "password" && factor?.strategy !== "passkey"
  const sendCode =
    !prepared &&
    (factor?.strategy === "email_code" || factor?.strategy === "phone_code")
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onCancel()
        }
      }}
    >
      <DialogContent size="md" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Verify it’s you</DialogTitle>
          <DialogDescription>
            Confirm your identity to continue with this account change.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {!verification && !error && (
            <p className="text-sm text-coval-text-muted">
              Loading verification methods…
            </p>
          )}
          {factors.length > 0 && (
            <>
              <Label htmlFor={methodSelectId}>Verification method</Label>
              <Select
                value={selected}
                disabled={busy}
                onValueChange={(next) => {
                  setSelected(next)
                  setValue("")
                  setPrepared(false)
                }}
              >
                <SelectTrigger
                  id={methodSelectId}
                  aria-label="Verification method"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {factors.map((item, index) => (
                    <SelectItem key={index} value={String(index)}>
                      {factorLabel(item)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!sendCode && factor?.strategy !== "passkey" && (
                <form
                  id="clerk-reverification"
                  onSubmit={(event) => {
                    event.preventDefault()
                    submit()
                  }}
                >
                  <Label htmlFor="reverification-value">
                    {needsCode ? "Verification code" : "Current password"}
                  </Label>
                  <Input
                    id="reverification-value"
                    type={needsCode ? "text" : "password"}
                    autoComplete={
                      needsCode ? "one-time-code" : "current-password"
                    }
                    value={value}
                    disabled={busy}
                    onChange={(event) => setValue(event.target.value)}
                  />
                </form>
              )}
            </>
          )}
          {verification && !factors.length && (
            <p role="alert" className="text-sm text-coval-danger-text">
              No supported verification method is available for this account.
              Contact support to continue.
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-coval-danger-text">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          {!verification && error ? (
            <Button
              disabled={busy}
              onClick={() =>
                session &&
                void handle(() => session.startVerification({ level }))
              }
            >
              Retry
            </Button>
          ) : (
            <Button
              loading={busy}
              disabled={
                busy ||
                !factor ||
                (!sendCode && factor.strategy !== "passkey" && !value)
              }
              onClick={submit}
            >
              {sendCode ? "Send code" : "Verify"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
